import React, { useEffect, useState, useCallback } from 'react';
import { reservationsApi, roomsApi } from '../../services/api';
import { fmt } from '../../utils/billing';

const STATUS_CFG = {
  'confirmed':   { bg: '#dbeafe', text: '#1d4ed8', dot: '#3b82f6', label: 'Confirmed' },
  'checked-in':  { bg: '#d1fae5', text: '#065f46', dot: '#10b981', label: 'Checked In' },
  'checked-out': { bg: '#f3f4f6', text: '#6b7280', dot: '#9ca3af', label: 'Checked Out' },
  'cancelled':   { bg: '#fee2e2', text: '#b91c1c', dot: '#ef4444', label: 'Cancelled' },
  'no-show':     { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b', label: 'No Show' },
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function isoDate(d) { return d.toISOString().slice(0, 10); }
function fmtDay(dateStr) {
  if (!dateStr) return '—';
  const clean = String(dateStr).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(clean)) return '—';
  return new Date(clean + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
// Parse total room count from room_type strings like "normal x5" or "Deluxe x2 + Standard x3"
function parseRoomCount(roomTypeStr) {
  if (!roomTypeStr) return 1;
  const matches = roomTypeStr.match(/x(\d+)/gi);
  if (matches && matches.length > 0) {
    return matches.reduce((sum, m) => sum + parseInt(m.slice(1), 10), 0);
  }
  return 1;
}

export default function CalendarPage({ hotel }) {
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [reservations, setReservations] = useState([]);
  const [totalRooms, setTotalRooms]     = useState(0);
  const [loading, setLoading]           = useState(false);
  const [selectedDay, setSelectedDay]   = useState(null);
  const [viewMode, setViewMode]         = useState('month');

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow    = new Date(year, month, 1).getDay();
  const monthStart  = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const monthEnd    = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
  const monthLabel  = new Date(year, month, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  const todayStr    = isoDate(today);

  const load = useCallback(async () => {
    if (!hotel?.id || hotel.id === 'all') return;
    setLoading(true);
    try {
      const [resData, roomData] = await Promise.all([
        reservationsApi.list(hotel.id, null, null, null, monthStart, monthEnd),
        roomsApi.list(hotel.id),
      ]);
      setReservations(Array.isArray(resData) ? resData : []);
      setTotalRooms(Array.isArray(roomData) ? roomData.length : 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [hotel?.id, monthStart, monthEnd]);

  useEffect(() => { load(); }, [load]);

  function navMonth(delta) {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear()); setMonth(d.getMonth());
    setSelectedDay(null);
  }

  // Reservations staying on a given date (check_in <= date < check_out)
  function stayingOn(dateStr) {
    return reservations.filter(r => {
      if (r.status === 'cancelled') return false;
      const ci = r.check_in || r.checkIn || '';
      const co = r.check_out || r.checkOut || '';
      return ci <= dateStr && co > dateStr;
    });
  }

  function checkInsOn(dateStr) {
    return reservations.filter(r => {
      const ci = r.check_in || r.checkIn || '';
      return ci === dateStr && r.status !== 'cancelled';
    });
  }

  function checkOutsOn(dateStr) {
    return reservations.filter(r => {
      const co = r.check_out || r.checkOut || '';
      return co === dateStr && r.status !== 'cancelled';
    });
  }

  function occPct(occupied) {
    return totalRooms ? Math.round((occupied / totalRooms) * 100) : 0;
  }

  function occColor(pct) {
    if (pct >= 85) return '#ef4444';
    if (pct >= 50) return '#f59e0b';
    return '#10b981';
  }

  function handleDayClick(dateStr) {
    const staying  = stayingOn(dateStr);
    const checkIns  = checkInsOn(dateStr);
    const checkOuts = checkOutsOn(dateStr);
    setSelectedDay({ dateStr, staying, checkIns, checkOuts });
  }

  // ─── MONTH GRID ─────────────────────────────────────────────────────────────
  function renderGrid() {
    return (
      <div>
        {/* Header row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, marginBottom: 4 }}>
          {DAYS.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 800, color: 'var(--muted)', padding: '6px 0', textTransform: 'uppercase', letterSpacing: 0.5 }}>{d}</div>
          ))}
        </div>
        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
          {Array.from({ length: firstDow }).map((_, i) => <div key={`b${i}`} />)}
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
            const dateStr  = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const staying  = stayingOn(dateStr);
            const ins      = checkInsOn(dateStr);
            const occ      = staying.length;
            const pct      = occPct(occ);
            const color    = occColor(pct);
            const isToday  = dateStr === todayStr;
            const isSel    = selectedDay?.dateStr === dateStr;
            const isPast   = dateStr < todayStr;

            return (
              <div
                key={day}
                onClick={() => handleDayClick(dateStr)}
                style={{
                  borderRadius: 10,
                  padding: '7px 6px 6px',
                  cursor: 'pointer',
                  minHeight: 80,
                  border: isSel
                    ? '2px solid var(--accent)'
                    : isToday
                      ? '2px solid var(--gold)'
                      : '1px solid var(--border)',
                  background: isSel
                    ? 'rgba(99,102,241,0.07)'
                    : isToday
                      ? 'rgba(200,151,42,0.06)'
                      : 'var(--s1)',
                  opacity: isPast && !isToday ? 0.6 : 1,
                  transition: 'all 0.12s',
                  position: 'relative',
                  boxShadow: isSel ? '0 0 0 1px var(--accent)' : isToday ? '0 0 0 1px var(--gold)' : 'none',
                }}
              >
                {/* Day number + check-in badge */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 5 }}>
                  <div style={{ fontWeight: isToday ? 900 : 700, fontSize: 15, color: isToday ? 'var(--gold)' : isSel ? 'var(--accent)' : 'var(--text)' }}>
                    {day}
                  </div>
                  {ins.length > 0 && (
                    <div style={{ fontSize: 9, fontWeight: 800, background: '#dbeafe', color: '#1d4ed8', borderRadius: 99, padding: '1px 5px', lineHeight: '14px' }}>
                      +{ins.length}
                    </div>
                  )}
                </div>

                {/* TODAY label */}
                {isToday && (
                  <div style={{ fontSize: 8, fontWeight: 800, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>TODAY</div>
                )}

                {/* Occupied count — big and clear */}
                {totalRooms > 0 && (
                  <div style={{ marginBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                      <span style={{ fontSize: 18, fontWeight: 900, color: occ > 0 ? color : 'var(--muted)', lineHeight: 1 }}>{occ}</span>
                      <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600 }}>/{totalRooms}</span>
                    </div>
                    <div style={{ fontSize: 9, color: color, fontWeight: 700, marginTop: 1 }}>
                      {occ === 0 ? 'vacant' : `${pct}% full`}
                    </div>
                    {/* Color bar */}
                    <div style={{ height: 3, borderRadius: 99, background: 'var(--border)', overflow: 'hidden', marginTop: 3 }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99 }} />
                    </div>
                  </div>
                )}

                {/* Status dots */}
                {occ > 0 && (
                  <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 2 }}>
                    {staying.slice(0, 5).map((r, i) => {
                      const cfg = STATUS_CFG[r.status] || STATUS_CFG['confirmed'];
                      return <div key={i} style={{ width: 5, height: 5, borderRadius: 99, background: cfg.dot }} />;
                    })}
                    {occ > 5 && <div style={{ fontSize: 8, color: 'var(--muted)', margin: '0 1px' }}>+{occ - 5}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap', paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          {[
            { color: '#10b981', label: 'Low (<50%)' },
            { color: '#f59e0b', label: 'Medium (50–84%)' },
            { color: '#ef4444', label: 'High (≥85%)' },
          ].map(l => (
            <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--muted)' }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: l.color }} />{l.label}
            </div>
          ))}
          {[
            { color: '#3b82f6', label: 'Confirmed' },
            { color: '#10b981', label: 'Checked In' },
            { color: '#9ca3af', label: 'Checked Out' },
          ].map(l => (
            <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--muted)' }}>
              <div style={{ width: 8, height: 8, borderRadius: 99, background: l.color }} />{l.label}
            </div>
          ))}
          <div style={{ fontSize: 11, color: '#3b82f6', marginLeft: 'auto' }}>
            <strong>+N</strong> = check-ins that day
          </div>
        </div>
      </div>
    );
  }

  // ─── LIST VIEW ─────────────────────────────────────────────────────────────
  function renderList() {
    const activeDays = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const staying = stayingOn(ds);
      const ins = checkInsOn(ds);
      const outs = checkOutsOn(ds);
      if (staying.length || ins.length || outs.length) activeDays.push({ ds, staying, ins, outs });
    }

    if (!activeDays.length) return (
      <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--muted)' }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>📅</div>
        <div style={{ fontWeight: 600 }}>No reservations in {monthLabel}</div>
      </div>
    );

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {activeDays.map(({ ds, staying, ins, outs }) => {
          const pct = occPct(staying.length);
          const color = occColor(pct);
          const isToday = ds === todayStr;
          return (
            <div key={ds} style={{ border: isToday ? '2px solid var(--gold)' : '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
              {/* Day header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: isToday ? 'rgba(200,151,42,0.06)' : 'var(--s1)', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontWeight: 900, fontSize: 15 }}>
                    {new Date(ds + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}
                    {isToday && <span style={{ fontSize: 10, background: 'var(--gold)', color: '#fff', borderRadius: 99, padding: '2px 7px', marginLeft: 8 }}>TODAY</span>}
                  </div>
                  {ins.length > 0 && <span style={{ fontSize: 11, background: '#dbeafe', color: '#1d4ed8', borderRadius: 99, padding: '2px 8px', fontWeight: 700 }}>↓ {ins.length} in</span>}
                  {outs.length > 0 && <span style={{ fontSize: 11, background: '#f3f4f6', color: '#6b7280', borderRadius: 99, padding: '2px 8px', fontWeight: 700 }}>↑ {outs.length} out</span>}
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color }}>
                  {staying.length}/{totalRooms} rooms · {pct}%
                </div>
              </div>
              {/* Guests */}
              {staying.map((r, i) => {
                const cfg = STATUS_CFG[r.status] || STATUS_CFG['confirmed'];
                const isCI = (r.check_in || r.checkIn) === ds;
                const isCO = (r.check_out || r.checkOut) === ds;
                return (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: i > 0 ? '1px solid var(--border)' : undefined, gap: 12 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <div style={{ width: 8, height: 8, borderRadius: 99, background: cfg.dot, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: 700 }}>{r.guest_name || r.guestName || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                          Room <strong>{r.room || '—'}</strong>
                          {r.room_type || r.roomType ? ` · ${r.room_type || r.roomType}` : ''}
                          {isCI && <span style={{ marginLeft: 6, color: '#3b82f6', fontWeight: 700 }}>↓ Check-in</span>}
                          {isCO && <span style={{ marginLeft: 6, color: '#6b7280', fontWeight: 700 }}>↑ Check-out</span>}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 99, background: cfg.bg, color: cfg.text, fontWeight: 700 }}>{cfg.label}</span>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                        {fmtDay(r.check_in || r.checkIn)} → {fmtDay(r.check_out || r.checkOut)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  // ─── DAY DETAIL PANEL ───────────────────────────────────────────────────────
  function renderDayPanel() {
    if (!selectedDay) return null;
    const { dateStr, staying, checkIns, checkOuts } = selectedDay;
    const pct    = occPct(staying.length);
    const color  = occColor(pct);
    const isToday = dateStr === todayStr;
    const avail  = Math.max(0, totalRooms - staying.length);

    return (
      <div style={{ width: 320, flexShrink: 0 }}>
        {/* Date header */}
        <div className="panel" style={{ borderRadius: 18, padding: 18, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {isToday ? '📍 Today' : 'Selected Day'}
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, marginTop: 4 }}>
                {new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long' })}
              </div>
            </div>
            <button onClick={() => setSelectedDay(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--muted)' }}>✕</button>
          </div>

          {/* Big occupancy number */}
          <div style={{ background: 'var(--s1)', borderRadius: 14, padding: '14px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 6 }}>Room Occupancy</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
              <span style={{ fontSize: 36, fontWeight: 900, color, lineHeight: 1 }}>{staying.length}</span>
              <span style={{ fontSize: 16, color: 'var(--muted)' }}>/ {totalRooms}</span>
              <span style={{ fontSize: 20, fontWeight: 800, color, marginLeft: 4 }}>{pct}%</span>
            </div>
            <div style={{ height: 8, borderRadius: 99, background: 'var(--border)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99, transition: 'width 0.3s' }} />
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
              {avail} room{avail !== 1 ? 's' : ''} available
            </div>
          </div>

          {/* 4 stat tiles */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { icon: '🛏️', label: 'Staying', value: staying.length,   color: '#10b981' },
              { icon: '🛎️', label: 'Check-in',value: checkIns.length,  color: '#3b82f6' },
              { icon: '🚪', label: 'Check-out',value: checkOuts.length, color: '#9ca3af' },
              { icon: '✅', label: 'Available',value: avail,            color: 'var(--accent)' },
            ].map(s => (
              <div key={s.label} style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: 20 }}>{s.icon}</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: s.color, lineHeight: 1.1, marginTop: 2 }}>{s.value}</div>
                <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Guest list */}
        <div className="panel" style={{ borderRadius: 18, overflow: 'hidden', padding: 0 }}>
          <div style={{ padding: '12px 16px', fontWeight: 800, fontSize: 14, borderBottom: '1px solid var(--border)' }}>
            🏨 Guests Staying ({staying.length})
          </div>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {staying.length === 0 ? (
              <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🌙</div>
                No guests on this day
              </div>
            ) : staying.map((r, i) => {
              const cfg   = STATUS_CFG[r.status] || STATUS_CFG['confirmed'];
              const isCI  = String(r.check_in || r.checkIn || '').slice(0, 10) === dateStr;
              const roomStr  = r.room || '';
              const roomNums = roomStr.split(',').map(s => s.trim()).filter(Boolean);
              const rtype    = r.room_type || r.roomType || '';
              // Count from assigned rooms, else parse from type string ("normal x5" → 5), else r.roomsCount
              const roomCount = roomNums.length > 0
                ? roomNums.length
                : (parseRoomCount(rtype) || r.rooms_count || r.roomsCount || 1);
              const checkIn  = String(r.check_in || r.checkIn || '').slice(0, 10);
              const checkOut = String(r.check_out || r.checkOut || '').slice(0, 10);
              // Strip the "x5" suffix from room type for cleaner display
              const rtypeDisplay = rtype.replace(/\s*x\d+/gi, '').trim();
              return (
                <div key={r.id} style={{ padding: '14px 16px', borderTop: i > 0 ? '1px solid var(--border)' : undefined }}>
                  {/* Guest name + status */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{r.guest_name || r.guestName || '—'}</div>
                    <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 99, background: cfg.bg, color: cfg.text, fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0 }}>{cfg.label}</span>
                  </div>

                  {/* Rooms booked */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 800, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 5 }}>
                      🚪 {roomNums.length > 0 ? roomNums.join(', ') : <span style={{ color: 'var(--muted)', fontWeight: 600 }}>Not assigned</span>}
                    </div>
                    <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 800, color: 'var(--accent)' }}>
                      {roomCount} room{roomCount !== 1 ? 's' : ''}
                    </div>
                  </div>

                  {/* Room type */}
                  {rtypeDisplay && (
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                      Type: <strong style={{ color: 'var(--text)' }}>{rtypeDisplay}</strong>
                    </div>
                  )}

                  {/* Check-in / Check-out dates */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                    <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: 6, padding: '2px 7px', fontWeight: 700, fontSize: 11 }}>In {fmtDay(checkIn)}</span>
                    <span style={{ color: 'var(--muted)' }}>→</span>
                    <span style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 6, padding: '2px 7px', fontWeight: 700, fontSize: 11 }}>Out {fmtDay(checkOut)}</span>
                  </div>

                  {/* Arrives today marker — only valid for staying guests */}
                  {isCI && (
                    <div style={{ marginBottom: 4 }}>
                      <span style={{ fontSize: 10, background: '#dbeafe', color: '#1d4ed8', borderRadius: 99, padding: '2px 8px', fontWeight: 800 }}>↓ Arrives Today</span>
                    </div>
                  )}

                  {/* Amount */}
                  {r.total_amount > 0 && (
                    <div style={{ fontSize: 13, color: '#b45309', fontWeight: 800, marginTop: 4 }}>{fmt(r.total_amount)}</div>
                  )}
                 </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ─── SUMMARY CARDS ──────────────────────────────────────────────────────────
  function renderSummary() {
    const active   = reservations.filter(r => r.status !== 'cancelled');
    const inHouse  = reservations.filter(r => r.status === 'checked-in');
    const upcoming = reservations.filter(r => r.status === 'confirmed');
    const todayOcc = stayingOn(todayStr).length;
    const todayPct = occPct(todayOcc);

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 20 }}>
        {[
          { icon: '📋', label: 'Bookings This Month', value: active.length,  color: 'var(--text)' },
          { icon: '🛎️', label: 'In House Now',        value: inHouse.length, color: '#10b981' },
          { icon: '📅', label: 'Upcoming',            value: upcoming.length, color: '#3b82f6' },
          {
            icon: '🏨',
            label: "Today's Occupancy",
            value: totalRooms > 0 ? `${todayOcc}/${totalRooms}` : '—',
            sub: totalRooms > 0 ? `${todayPct}% full` : '',
            color: occColor(todayPct),
          },
          { icon: '🚪', label: 'Total Rooms',         value: totalRooms,     color: 'var(--accent)' },
        ].map(s => (
          <div key={s.label} className="panel" style={{ borderRadius: 14, padding: '14px 14px' }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
            {s.sub && <div style={{ fontSize: 11, fontWeight: 700, color: s.color, marginTop: 2 }}>{s.sub}</div>}
            <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>
    );
  }

  // ─── RENDER ─────────────────────────────────────────────────────────────────
  if (!hotel || hotel.id === 'all') {
    return (
      <div className="empty" style={{ marginTop: 60 }}>
        <div className="empty-icon">📅</div>
        <div style={{ fontSize: 15, fontWeight: 600 }}>Select a specific hotel to view the calendar</div>
      </div>
    );
  }

  return (
    <div className="animate-in">
      {/* Header */}
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontWeight: 900, fontSize: 30 }}>🗓️ Reservation Calendar</h1>
          <p style={{ margin: '5px 0 0', color: 'var(--muted)', fontSize: 13 }}>{hotel.name} · room occupancy at a glance</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* View toggle */}
          <div style={{ display: 'flex', background: 'var(--s1)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
            {[['month', '📆 Month'], ['list', '📋 List']].map(([v, lbl]) => (
              <button key={v} onClick={() => setViewMode(v)} style={{
                padding: '7px 14px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 800,
                background: viewMode === v ? 'var(--accent)' : 'transparent',
                color: viewMode === v ? '#fff' : 'var(--muted)',
                transition: 'all 0.12s',
              }}>{lbl}</button>
            ))}
          </div>
          <button className="btn btn-ghost" onClick={() => navMonth(-1)}>‹ Prev</button>
          <button className="btn btn-gold" onClick={() => { setMonth(today.getMonth()); setYear(today.getFullYear()); setSelectedDay(null); }} style={{ fontSize: 12, padding: '8px 14px' }}>Today</button>
          <button className="btn btn-ghost" onClick={() => navMonth(1)}>Next ›</button>
          <button className="btn btn-ghost" onClick={load} title="Refresh" style={{ fontSize: 14 }}>🔄</button>
        </div>
      </div>

      {/* Month label */}
      <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 14 }}>{monthLabel}</div>

      {/* Summary */}
      {renderSummary()}

      {loading ? (
        <div className="panel" style={{ borderRadius: 20, padding: 60, textAlign: 'center', color: 'var(--muted)' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
          Loading reservations…
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="panel" style={{ borderRadius: 20, padding: 18 }}>
              {viewMode === 'month' ? renderGrid() : renderList()}
            </div>
          </div>
          {selectedDay && viewMode === 'month' && renderDayPanel()}
        </div>
      )}
    </div>
  );
}
